import '@arcgis/core/assets/esri/themes/dark/main.css';

import {
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  SetStateAction,
  useState,
  MutableRefObject,
} from 'react';
import { UscgMapContext } from './uscgMapContextObject';
import { matchPath, useLocation } from 'react-router';
import Search from '@arcgis/core/widgets/Search';
import LocatorSearchSource from '@arcgis/core/widgets/Search/LocatorSearchSource';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import WebMap from '@arcgis/core/WebMap';
import type { ArcgisMapView } from './utils/arcgisView';
import {
  mapCenterZoomFromViewpoint,
  readMapViewMode,
  sceneCameraFromViewpoint,
  snapshotArcgisViewpoint,
  writeMapViewMode,
  type MapViewMode,
  type MapViewpoint,
} from './utils/mapViewMode';
import { applyZoomOutConstraints } from './utils/mapZoomOutConstraints';
import { HYBRID } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisTypes';
import { useCurrentGroup } from 'components/IAP/hooks/commonHooks';
import { useRefreshArcgisLinkedLocations } from '../ResourceTab/useRefreshArcgisLinkedLocations';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';
import {
  UscgSymbolDrawingReturnType,
  useUscgSymbolDrawing,
} from './utils/useUscgSymbolDrawing';
import { useInternalLayerSync } from './utils/useInternalLayerSync';
import { useDefaultInternalLayerVersionSelection } from './utils/useDefaultInternalLayerVersionSelection';
import {
  zoomMapViewToLocation,
  zoomMapViewToGraphic,
} from './utils/mapViewZoom';
import {
  applyStoredAttributePopup,
  buildFallbackFeatureGraphic,
  focusFeatureOnMap as focusFeatureOnMapUtil,
} from './utils/focusFeatureOnMap';
import {
  queryExternalLayerFeatures,
  type ExternalLayerFeature,
} from './utils/queryExternalLayerFeatures';
import { collectFeatureLayers } from './utils/useExternalLayerTimeExtent';
import { WGS84_SPATIAL_REFERENCE } from './utils/spatialReference';
import { useUscgAorLayerSync } from './utils/useUscgAorLayerSync';
import { usePowerOutageLayerSync } from './utils/usePowerOutageLayerSync';
import { useEriLayerSync } from './utils/useEriLayerSync';
import { useIncidentAreaLayerSync } from './utils/useIncidentAreaLayerSync';
import { useTakLayerSync } from './utils/useTakLayerSync';
import { useListTakEntitiesQuery } from 'hooks/takEntity/useListTakEntitiesQuery';
import { useExternalLayerSync } from './utils/useExternalLayerSync';
import { useIncidentLayerSync } from './utils/useIncidentLayerSync';
import { useActionLayerSync } from './utils/useActionLayerSync';
import { useIncidentActions } from 'components/IAP/hooks/incidentActionHooks';
import { useOrgActions } from 'components/IAP/hooks/orgActionHooks';
import { INCIDENT, ORGANIZATION } from 'components/IAP/ICSConstants';
import {
  useEquinorFlag,
  useGovernmentOfOntarioFlag,
  usePowerOutagesFlag,
  useTennesseeNationalGuardFlag,
  useTropicalDemoKind,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import { EQUINOR_DIVISIONS_LAYER_NAME } from 'components/MapPage/CorporateViewTabs/NotificationsTab/equinorGraneDivisions';
import { JAPAN_MAP_EXTENT } from 'components/MapPage/CorporateViewTabs/NotificationsTab/japanEarthquakeNotification';
import Extent from '@arcgis/core/geometry/Extent';
import {
  EQUINOR_GRANE_ASSETS_LAYER_TITLE,
  EQUINOR_OIL_SLICK_LAYER_TITLE,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/equinorGraneSpillNotification';
import { ONTARIO_GTA_STORM_LAYER_NAME } from 'components/MapPage/CorporateViewTabs/NotificationsTab/ontarioSnowstormLayerData';
import type {
  ExternalLayer,
  ExternalLayerAccessType,
} from 'hooks/externalLayer/types';
import type { InternalLayer } from 'hooks/internalLayer/types';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import {
  useAllIncidents,
  useExactIncident,
} from 'components/IAP/hooks/incidentHooks';
import { useLocationGraphicLayer } from './utils/useLocationGraphicLayer';
import type { Geometry } from 'geojson';
import { US_INITIAL_VIEW } from './utils/constants';
import * as reactiveUtil from '@arcgis/core/core/reactiveUtils';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useAors, useAorDescendants } from 'hooks/useAors';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { findAorByHomeId } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/findAorByHomeId';
import { performZoomToHomeAor } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/useAutoZoomToHomeAor';
import { useArcGisAuthorization } from './utils/useArcGisAuthorization';
import { useEnsureTennesseeNationalGuardLayers } from './arcgis-integration/useEnsureTennesseeNationalGuardLayers';
import { useEnsureHistoricalHurricanesDemo } from './cfa-demo/useEnsureHistoricalHurricanesDemo';
import { useEnsureOrgAssetsLayer } from './useEnsureOrgAssetsLayer';
import { useAssetSymbolVisibilitySync } from './useAssetSymbolVisibilitySync';
import { useEnsureHeleneConeResourceSets } from './cfa-demo/useEnsureHeleneConeResourceSets';
import { useEnsureHeleneConeOrgEvent } from './cfa-demo/useEnsureHeleneConeOrgEvent';
import { useEnsureChickfilaLocationsLayer } from './utils/useEnsureChickfilaLocationsLayer';
import { useEnsureUsaCurrentWildfiresLayer } from './arcgis-integration/useEnsureUsaCurrentWildfiresLayer';
import { useEnsureMemphisThreatOrgActions } from 'components/IAP/cfa-demo/useEnsureMemphisThreatOrgActions';
import { useEnsureUscgFocSeThreatOrgActions } from 'components/IAP/foc-demo/useEnsureUscgFocSeThreatOrgActions';
import { useEnsureUscgFocSeResourceSets } from './foc-demo/useEnsureUscgFocSeResourceSets';
import { useEnsureUscgFocAors } from './foc-demo/useEnsureUscgFocAors';
import { useEnsureUscgFocSeOrgEvent } from './foc-demo/useEnsureUscgFocSeOrgEvent';
import {
  useEnsureUscgFocSeStormLayer,
  USCG_FOC_SE_STORM_LOCATION_NAME,
} from './foc-demo/useEnsureUscgFocSeStormLayer';
import { USCG_FOC_SE_STORM_LAYER_NAME } from './foc-demo/uscgFocSoutheastTropicalData';
import { useEnsureTennesseeMemphisOrgActions } from 'components/IAP/cfa-demo/useEnsureTennesseeMemphisOrgActions';
import { useEnsureTennesseeMemphisResourceSets } from './cfa-demo/useEnsureTennesseeMemphisResourceSets';
import { useEnsureTennesseeMemphisOrgEvent } from './cfa-demo/useEnsureTennesseeMemphisOrgEvent';
import { useEnsureTennesseeMemphisAssetLayer } from './cfa-demo/useEnsureTennesseeMemphisAssetLayer';
import { useEnsurePublicExternalLayers } from './arcgis-integration/useEnsurePublicExternalLayers';
import { useEnsureEpaGulfOrgActions } from 'components/IAP/cfa-demo/useEnsureEpaGulfOrgActions';
import { useEnsureEpaGulfResourceSets } from './cfa-demo/useEnsureEpaGulfResourceSets';
import { useEnsureEpaGulfOrgEvent } from './cfa-demo/useEnsureEpaGulfOrgEvent';
import { useEnsureDhsRegion4ResourceSets } from './cfa-demo/useEnsureDhsRegion4ResourceSets';
import { useEnsureDhsRegion4OrgEvent } from './cfa-demo/useEnsureDhsRegion4OrgEvent';
import { useEnsureDhsTropicalAutomationRule } from '../EventsTab/useEnsureDhsTropicalAutomationRule';
import { useEnsureCnicResourceSets } from './cfa-demo/useEnsureCnicResourceSets';
import { useEnsureCnicOrgEvent } from './cfa-demo/useEnsureCnicOrgEvent';
import { useEnsureCnicTropicalAutomationRule } from '../EventsTab/useEnsureCnicTropicalAutomationRule';
import { useEnsureCnicHawaiiStormLayer } from './cfa-demo/useEnsureCnicHawaiiStormLayer';
import { useEnsureOntarioWinterAutomationRule } from '../EventsTab/useEnsureOntarioWinterAutomationRule';
import { useEnsureOntarioGtaResourceSets } from './cfa-demo/useEnsureOntarioGtaResourceSets';
import { useEnsureOntarioGtaOrgEvent } from './cfa-demo/useEnsureOntarioGtaOrgEvent';
import { useEnsureOntarioGtaStormLayer } from './cfa-demo/useEnsureOntarioGtaStormLayer';
import { useEnsureEquinorGraneResourceSets } from './cfa-demo/useEnsureEquinorGraneResourceSets';
import { useEnsureEquinorGraneOrgEvent } from './cfa-demo/useEnsureEquinorGraneOrgEvent';
import { useEnsureEquinorGraneIncident } from './cfa-demo/useEnsureEquinorGraneIncident';
import { useEnsureEquinorAutomationRule } from '../EventsTab/useEnsureEquinorAutomationRule';
import { useEnsureJapanResourceSets } from './cfa-demo/useEnsureJapanResourceSets';
import { useEnsureJapanOrgEvent } from './cfa-demo/useEnsureJapanOrgEvent';
import {
  JAPAN_INCIDENT_LAYER_NAMES,
  useEnsureJapanIncident,
} from './cfa-demo/useEnsureJapanIncident';
import { useEnsureJapanAutomationRule } from '../EventsTab/useEnsureJapanAutomationRule';
import { useEnsureFacilityDemoAssetOrgActions } from 'components/IAP/cfa-demo/useEnsureFacilityDemoAssetOrgActions';
import { isExternalLayerInCurrentScope } from './utils/isExternalLayerInCurrentScope';
import { useDefaultLayerVisibilityById } from './utils/useDefaultLayerVisibilityById';
import { useAiMapPick } from './utils/useAiMapPick';
import {
  buildMapLayerCatalog,
  type MapLayerCatalogItem,
} from './utils/mapLayerCatalog';
import type { IncidentListItemData } from '../IncidentsTab/IncidentListItem';
import { buildIncidentListItems } from '../IncidentsTab/utils/buildIncidentListItems';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Point from '@arcgis/core/geometry/Point';
import type { ActionAreaFilter } from 'components/IAP/utils/actionSpatialFilter';
import type {
  AiDrawnAreaHandler,
  AiDrawnAreaPayload,
  AiLayerPickHandler,
  AiLayerPickPayload,
} from './utils/aiMapPick.types';

export type {
  AiDrawnAreaHandler,
  AiDrawnAreaPayload,
  AiLayerPickHandler,
  AiLayerPickPayload,
} from './utils/aiMapPick.types';
import type {
  LegendClassFilter,
  LayerDefinitionExpressionParts,
} from './utils/legendClassFilter';
import {
  buildLegendClassDefinitionExpression,
  composeDefinitionExpressionParts,
} from './utils/legendClassFilter';

export type {
  LegendClassFilter,
  LayerDefinitionExpressionParts,
} from './utils/legendClassFilter';

const USCG_PORTAL_ITEM_ID = '7899d4c1ae2a4eaea752e48c17b62f0b';
const MAP_BENCH_ID = 'map-bench';
const MAP_BENCH_WIDTH = 400;
const MAP_BENCH_HEIGHT = 400;
/**
 * Zoom level when focusing the map on a layer centroid via the Layers-tab map
 * icon.
 */
const LAYER_CENTROID_ZOOM = 10;

export type UscgMapType = 'uscg' | 'hybrid';

type PreviewLayer = {
  id: string | nullish;
  url: string | nullish;
  authType?: ExternalLayerAccessType;
};

/** Active map time-slider window for an external layer (e.g. Ting Outages). */
export type ExternalLayerTimeFilter = {
  startIso: string;
  endIso: string;
  label: string;
};

export type VisibleMapLayerItem = {
  id: string;
  name: string;
  kind: 'External' | 'Internal';
};

/** Preview layers carry only the fields the map needs to add them. */
export type PreviewExternalLayerRef = Pick<
  ExternalLayer,
  | 'id'
  | 'arcgisItemId'
  | 'serviceUrl'
  | 'incident'
  | 'incidentId'
  | 'authType'
  | 'defaultVisible'
>;

export type AvailableMapLayerItem = MapLayerCatalogItem<
  ExternalLayer | PreviewExternalLayerRef,
  InternalLayer
>;

export type UscgMapContextType = {
  mapView: ArcgisMapView | null;
  mapViewElementRef: MutableRefObject<HTMLDivElement | null>;
  isMapVisible: boolean;
  setIsMapVisible: Dispatch<SetStateAction<boolean>>;
  mapType: UscgMapType;
  setMapType: (mapType: UscgMapType) => void;
  mapViewMode: MapViewMode;
  setMapViewMode: (mode: MapViewMode) => void;
  toggleExternalLayerVisibility: (layerId: string) => void;
  isExternalLayerVisible: (layerId: string) => boolean;
  toggleInternalLayerVisibility: (layerId: string) => void;
  setInternalLayerVisible: (layerId: string, visible: boolean) => void;
  isInternalLayerVisible: (layerId: string) => boolean;
  /** Catalog layers currently visible on the map (internal + external). */
  visibleMapLayerItems: VisibleMapLayerItem[];
  /** Every layer in scope for the map, visible or not, grouped by source. */
  availableMapLayerItems: AvailableMapLayerItem[];
  // Shared AOR filter (applied to both sidebar and map)
  aorFilterIds: string[] | null;
  setAorFilterIds: Dispatch<SetStateAction<string[] | null>>;

  /** Time-slider windows keyed by external layer id (for AI Layers datasource). */
  externalLayerTimeFilterById: Record<string, ExternalLayerTimeFilter>;
  setExternalLayerTimeFilter: (
    layerId: string,
    filter: ExternalLayerTimeFilter | null
  ) => void;

  /**
   * Unique-value legend class filters keyed by external layer id. When set, map
   * FeatureLayers get a composed definitionExpression and AI selections receive
   * matching attributeFilters.
   */
  legendClassFilterByLayerId: Record<string, LegendClassFilter>;
  setLegendClassFilter: (
    layerId: string,
    filter: LegendClassFilter | null
  ) => void;
  /**
   * Set/clear the time half of a composed definitionExpression (Ting date
   * mode).
   */
  setLayerDefinitionExpressionTimePart: (
    layerId: string,
    expression: string | null
  ) => void;

  /**
   * When true, Layers-tab row / legend class / map polygon clicks (or Draw
   * area) add a layer as a Pratus AI data source (see ChatDisasterGPT).
   */
  aiLayerPickMode: boolean;
  setAiLayerPickMode: (enabled: boolean) => void;
  registerAiLayerPickHandler: (handler: AiLayerPickHandler | null) => void;
  /** Invoke the registered chat handler and exit pick mode. */
  pickAiLayer: (payload: AiLayerPickPayload) => void;
  registerAiDrawnAreaHandler: (handler: AiDrawnAreaHandler | null) => void;
  pickAiDrawnArea: (payload: AiDrawnAreaPayload) => void;
  registerAiDrawnAreaCancelHandler: (handler: (() => void) | null) => void;
  /** Report Edit-on-map hosts Cancel draw; hide the map overlay button. */
  areaDrawControlsOnMap: boolean;
  /** Currently visible map layers (for drawn-area fallback). */
  getVisibleLayersForAI: () => AiLayerPickPayload[];
  startAiAreaDraw: () => void;
  cancelAiAreaDraw: () => void;
  aiAreaDrawActive: boolean;
  showReportGeofence: (
    geometry: Geometry,
    label?: string,
    ownerId?: string
  ) => void;
  clearReportGeofence: (ownerId?: string) => void;

  incidentFilterId: string | null;
  setIncidentFilterId: (id: string | null) => void;

  hiddenIncidentIds: string[];
  setHiddenIncidentIds: Dispatch<SetStateAction<string[]>>;
  toggleIncidentHiddenOnMap: (incidentId: string) => void;

  hiddenActionIds: string[];
  setHiddenActionIds: Dispatch<SetStateAction<string[]>>;
  toggleActionHiddenOnMap: (actionId: string) => void;

  /**
   * The table and the action layer are two views of one list, so filtering,
   * selecting and clicking in either has to read back in the other.
   */
  visibleActionIds: string[] | null;
  setVisibleActionIds: Dispatch<SetStateAction<string[] | null>>;
  selectedActionIds: string[];
  setSelectedActionIds: Dispatch<SetStateAction<string[]>>;
  /** Set when a map graphic is clicked, so the table can scroll to that row. */
  focusedActionId: string | null;
  setFocusedActionId: Dispatch<SetStateAction<string | null>>;
  /** The area the user drew to filter tasks by location, in WGS84 GeoJSON. */
  actionAreaFilter: ActionAreaFilter | null;
  setActionAreaFilter: Dispatch<SetStateAction<ActionAreaFilter | null>>;

  toggleAorVisibility: () => void;
  isAorVisible: boolean;
  setIsAorVisible: (visible: boolean) => void;

  /** The shapes drawn on the 215A's incident areas. */
  toggleIncidentAreaVisibility: () => void;
  isIncidentAreaVisible: boolean;

  toggleTakVisibility: () => void;
  isTakVisible: boolean;

  /**
   * County choropleth of the latest PowerOutage.us snapshot (POWER_OUTAGES
   * flag).
   */
  togglePowerOutageVisibility: () => void;
  isPowerOutageVisible: boolean;

  /**
   * County choropleth of the latest ERI (outage prediction) model run
   * (POWER_OUTAGES flag).
   */
  toggleEriVisibility: () => void;
  isEriVisible: boolean;
  /**
   * Requested ERI forecast step (ISO); null lets the server pick the next
   * upcoming hour.
   */
  eriStep: string | null;
  setEriStep: (step: string | null) => void;

  zoomToLocation: (params: {
    longitude: number;
    latitude: number;
    zoom?: number;
  }) => Promise<void> | undefined;

  zoomToGraphic: (params: {
    graphic: __esri.Graphic;
    zoom?: number;
  }) => Promise<void> | undefined;
  zoomToExternalLayer: (params: {
    externalLayerId: string;
  }) => Promise<void> | undefined;
  zoomToInternalLayer: (params: {
    internalLayerId: string;
  }) => Promise<void> | undefined;
  /** Zoom to layer centroid and open ArcGIS popup (name + last updated). */
  focusLayerOnMap: (params: {
    kind: 'external' | 'internal';
    layerId: string;
    name: string;
    lastUpdated?: string | null;
  }) => Promise<void>;

  /** Zoom to a feature graphic and open its ArcGIS feature popup. */
  focusFeatureOnMap: (params: {
    graphic: __esri.Graphic;
    layer?: { kind: 'external' | 'internal'; layerId: string };
  }) => Promise<void>;

  /** Find a live internal-layer graphic by feature id (ics location id). */
  getInternalFeatureGraphic: (
    internalLayerId: string,
    featureId: string
  ) => __esri.Graphic | null;

  /**
   * Resolve a layer feature (live map graphic preferred, citation geometry
   * fallback), then zoom + open its ArcGIS popup. Used by feature-list icons
   * and AI layer citations.
   */
  focusLayerFeatureOnMap: (params: {
    kind: 'external' | 'internal';
    layerId: string;
    featureId: string;
    geometry?: unknown;
    attributes?: Record<string, unknown> | null;
    title?: string | null;
    /**
     * When the map layer is a group, only this sublayer is queried. Hurricane
     * packs share one group, and OBJECTID collides across its sublayers.
     */
    mapLayerTitle?: string | null;
  }) => Promise<void>;

  getExternalLayerFeatures: (
    externalLayerId: string
  ) => Promise<ExternalLayerFeature[]>;
  /** ArcGIS runtime layers currently added for a persisted external layer id. */
  getAddedExternalLayers: (layerId: string) => __esri.Layer[] | undefined;
  isExternalLayerLoadError: (externalLayerId: string) => boolean;
  externalLayerLoadErrorById: Map<string, boolean>;

  selectedInternalLayerVersionByInternalLayerId: Record<
    string,
    InternalLayerVersion
  >;
  getSelectedVersionForLayer: (
    layerId: string
  ) => InternalLayerVersion | undefined;
  setSelectedInternalLayerVersion: (
    layerId: string,
    version: InternalLayerVersion
  ) => void;
  sidebarSelectedInternalLayer: InternalLayer | null;
  setSidebarSelectedInternalLayer: (layer: InternalLayer | null) => void;
  isLoadingInternalFeatures: boolean;
  isArcGISAuthorized: boolean;
  isCheckingArcGISAuthorization: boolean;
  authorizeArcGIS: () => Promise<void>;
  setIsArcGISAuthorized: (authorized: boolean) => void;
  isPublicExternalLayersReady: boolean;
  isProtectedExternalLayersReady: boolean;
  isExternalLayersReady: boolean;
  captureMapSketchFile: () => Promise<File | undefined>;

  previewLayers: PreviewLayer[];
  setPreviewLayers: Dispatch<SetStateAction<PreviewLayer[]>>;
} & Pick<
  UscgSymbolDrawingReturnType,
  | 'startDrawing'
  | 'setPolygonDrawingColor'
  | 'setPolygonDrawingOpacity'
  | 'stopDrawing'
  | 'addPendingPoint'
  | 'addPendingPolyline'
  | 'addPendingPolygon'
  | 'clearPendingGraphics'
  | 'removePendingGraphicsByPendingGraphicId'
  | 'symbolDrawingState'
  | 'connectDrawingPoints'
  | 'applyTypedCoordinates'
  | 'resetDrawing'
  | 'getDrawnGraphic'
  | 'beginEditingLocation'
  | 'describeDrawnGraphic'
>;

export const useUscgMapContext = (): UscgMapContextType => {
  const context = useContext(UscgMapContext) as UscgMapContextType | undefined;
  if (context === undefined) {
    throw new Error(
      'useUscgMapContext must be used within a UscgMapContextProvider'
    );
  }

  return context;
};

/** Returns null when rendered outside the map provider (e.g. IAP-only drawers). */
export const useOptionalUscgMapContext = (): UscgMapContextType | null => {
  return (useContext(UscgMapContext) as UscgMapContextType | undefined) ?? null;
};

let japanFramedThisSession = false;

export const UscgMapContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  useRefreshArcgisLinkedLocations();
  const mapViewElementRef = useRef<HTMLDivElement | null>(null);
  const addedExternalLayersRef = useRef<Map<string, __esri.Layer[]>>(new Map());
  // Keep track of state that shouldn't trigger a re-render, prevent useEffect infinite loop.
  // The Map object from ArcGIS Maps SDK will handle the visual updates
  const internalGraphicsLayersRef = useRef<Map<string, __esri.GraphicsLayer>>(
    new Map()
  );
  const locationGraphicLayerRef = useRef<GraphicsLayer>(
    new GraphicsLayer({ title: 'Incident Location' })
  );
  const reportGeofenceLayerRef = useRef<GraphicsLayer>(
    new GraphicsLayer({ title: 'Report geofence' })
  );
  const reportGeofenceOwnerRef = useRef<string | null>(null);

  const [mapView, setMapView] = useState<ArcgisMapView | null>(null);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [mapType, setMapType] = useState<UscgMapType>('hybrid');
  const [mapViewMode, setMapViewModeState] =
    useState<MapViewMode>(readMapViewMode);
  const viewpointRef = useRef<MapViewpoint | null>(null);
  const setMapViewMode = useCallback((mode: MapViewMode) => {
    writeMapViewMode(mode);
    setMapViewModeState(mode);
  }, []);
  const [externalLayerVisibilityById, setExternalLayerVisibilityById] =
    useState<Map<string, boolean | null>>(() => new Map());
  const [externalLayerLoadErrorById, setExternalLayerLoadErrorById] = useState<
    Map<string, boolean>
  >(() => new Map());
  const [internalLayerVisibilityById, setInternalLayerVisibilityById] =
    useState<Map<string, boolean | null>>(() => new Map());
  const [isAorVisible, setIsAorVisible] = useState(false);
  const [isIncidentAreaVisible, setIsIncidentAreaVisible] = useState(false);
  const [isTakVisible, setIsTakVisible] = useState(false);
  const tennesseeNationalGuardFlag = useTennesseeNationalGuardFlag();
  const equinorEnabled = useEquinorFlag();
  const japanEnabled = useTropicalDemoKind() === 'japan';
  const ontarioEnabled = useGovernmentOfOntarioFlag();
  const uscgFocEnabled = useUscgFocRequirementsFlag();
  const takLayerActive = tennesseeNationalGuardFlag && isTakVisible;

  useEffect(() => {
    if (!tennesseeNationalGuardFlag) {
      setIsTakVisible(false);
    }
  }, [tennesseeNationalGuardFlag]);

  const [isPowerOutageVisible, setIsPowerOutageVisible] = useState(false);
  const [isEriVisible, setIsEriVisible] = useState(false);
  const [eriStep, setEriStep] = useState<string | null>(null);
  const powerOutagesFlag = usePowerOutagesFlag();
  const powerOutageLayerActive = powerOutagesFlag && isPowerOutageVisible;
  const eriLayerActive = powerOutagesFlag && isEriVisible;

  useEffect(() => {
    if (!powerOutagesFlag) {
      setIsPowerOutageVisible(false);
      setIsEriVisible(false);
    }
  }, [powerOutagesFlag]);

  const [aorFilterIds, setAorFilterIds] = useState<string[] | null>(null);
  const [externalLayerTimeFilterById, setExternalLayerTimeFilterById] =
    useState<Record<string, ExternalLayerTimeFilter>>({});
  const setExternalLayerTimeFilter = useCallback(
    (layerId: string, filter: ExternalLayerTimeFilter | null) => {
      setExternalLayerTimeFilterById((prev) => {
        if (filter === null) {
          if (!(layerId in prev)) return prev;
          const next = { ...prev };
          delete next[layerId];
          return next;
        }
        const existing = prev[layerId];
        if (
          existing &&
          existing.startIso === filter.startIso &&
          existing.endIso === filter.endIso &&
          existing.label === filter.label
        ) {
          return prev;
        }
        return { ...prev, [layerId]: filter };
      });
    },
    []
  );

  const [
    definitionExpressionPartsByLayerId,
    setDefinitionExpressionPartsByLayerId,
  ] = useState<Record<string, LayerDefinitionExpressionParts>>({});
  const [legendClassFilterByLayerId, setLegendClassFilterByLayerId] = useState<
    Record<string, LegendClassFilter>
  >({});

  const [aiLayerPickMode, setAiLayerPickModeState] = useState(false);
  const aiLayerPickHandlerRef = useRef<AiLayerPickHandler | null>(null);
  const aiDrawnAreaHandlerRef = useRef<AiDrawnAreaHandler | null>(null);
  const aiDrawnAreaCancelHandlerRef = useRef<(() => void) | null>(null);
  const [areaDrawControlsOnMap, setAreaDrawControlsOnMap] = useState(true);

  const setAiLayerPickMode = useCallback((enabled: boolean) => {
    setAiLayerPickModeState(enabled);
  }, []);

  const registerAiLayerPickHandler = useCallback(
    (handler: AiLayerPickHandler | null) => {
      aiLayerPickHandlerRef.current = handler;
    },
    []
  );

  const registerAiDrawnAreaHandler = useCallback(
    (handler: AiDrawnAreaHandler | null) => {
      aiDrawnAreaHandlerRef.current = handler;
    },
    []
  );

  const registerAiDrawnAreaCancelHandler = useCallback(
    (handler: (() => void) | null) => {
      aiDrawnAreaCancelHandlerRef.current = handler;
      setAreaDrawControlsOnMap(handler == null);
    },
    []
  );

  const pickAiLayer = useCallback((payload: AiLayerPickPayload) => {
    aiLayerPickHandlerRef.current?.(payload);
    setAiLayerPickModeState(false);
  }, []);

  const pickAiDrawnArea = useCallback((payload: AiDrawnAreaPayload) => {
    aiDrawnAreaHandlerRef.current?.(payload);
    setAiLayerPickModeState(false);
  }, []);

  const applyComposedDefinitionExpression = useCallback(
    (layerId: string, parts: LayerDefinitionExpressionParts | undefined) => {
      const layers = addedExternalLayersRef.current.get(layerId);
      const featureLayers = collectFeatureLayers(layers);
      const composed = composeDefinitionExpressionParts(parts);
      for (const fl of featureLayers) {
        fl.definitionExpression = composed ?? null;
      }
    },
    []
  );

  const setLayerDefinitionExpressionTimePart = useCallback(
    (layerId: string, expression: string | null) => {
      setDefinitionExpressionPartsByLayerId((prev) => {
        const existing = prev[layerId] ?? {};
        const nextParts: LayerDefinitionExpressionParts = {
          ...existing,
          time: expression,
        };
        if (!nextParts.time && !nextParts.legend) {
          if (!(layerId in prev)) return prev;
          const next = { ...prev };
          delete next[layerId];
          applyComposedDefinitionExpression(layerId, undefined);
          return next;
        }
        applyComposedDefinitionExpression(layerId, nextParts);
        return { ...prev, [layerId]: nextParts };
      });
    },
    [applyComposedDefinitionExpression]
  );

  const setLegendClassFilter = useCallback(
    (layerId: string, filter: LegendClassFilter | null) => {
      setLegendClassFilterByLayerId((prev) => {
        if (filter === null) {
          if (!(layerId in prev)) return prev;
          const next = { ...prev };
          delete next[layerId];
          return next;
        }
        return { ...prev, [layerId]: filter };
      });

      const legendExpr =
        filter && filter.values.length > 0
          ? buildLegendClassDefinitionExpression(filter.field, filter.values)
          : null;

      setDefinitionExpressionPartsByLayerId((prev) => {
        const existing = prev[layerId] ?? {};
        const nextParts: LayerDefinitionExpressionParts = {
          ...existing,
          legend: legendExpr,
        };
        if (!nextParts.time && !nextParts.legend) {
          if (!(layerId in prev)) {
            applyComposedDefinitionExpression(layerId, undefined);
            return prev;
          }
          const next = { ...prev };
          delete next[layerId];
          applyComposedDefinitionExpression(layerId, undefined);
          return next;
        }
        applyComposedDefinitionExpression(layerId, nextParts);
        return { ...prev, [layerId]: nextParts };
      });
    },
    [applyComposedDefinitionExpression]
  );
  const [stateIncidentFilterId, setIncidentFilterId] = useState<string | null>(
    null
  );
  const [
    selectedInternalLayerVersionByInternalLayerId,
    setSelectedInternalLayerVersionByInternalLayerId,
  ] = useState<Record<string, InternalLayerVersion>>({});
  const [sidebarSelectedInternalLayer, setSidebarSelectedInternalLayer] =
    useState<InternalLayer | null>(null);
  const {
    isArcGISAuthorized,
    isCheckingArcGISAuthorization,
    authorizeArcGIS,
    setIsArcGISAuthorized,
  } = useArcGisAuthorization();

  const [isPublicExternalLayersReady, setIsPublicExternalLayersReady] =
    useState(false);
  const [isProtectedExternalLayersReady, setIsProtectedExternalLayersReady] =
    useState(false);
  const [isExternalLayersReady, setIsExternalLayersReady] = useState(false);

  const [previewLayers, setPreviewLayers] = useState<PreviewLayer[]>([]);

  const userGuid = useAppSelector(selectUserGuid);
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: roster } = useGetRostersForGroup(groupGuid);
  const { data: aors } = useAors();

  const currentUserRoster = roster?.find((r) => r.user_guid === userGuid);
  const homeAorId = currentUserRoster?.home_aor ?? null;
  const resolvedHomeAorId = findAorByHomeId(aors, homeAorId)?.id ?? null;
  const { data: homeAorDescendants, isLoading: isHomeAorDescendantsLoading } =
    useAorDescendants(resolvedHomeAorId);

  const {
    startDrawing,
    setPolygonDrawingColor,
    setPolygonDrawingOpacity,
    stopDrawing,
    addPendingPoint,
    addPendingPolyline,
    addPendingPolygon,
    clearPendingGraphics,
    removePendingGraphicsByPendingGraphicId,
    symbolDrawingState,
    connectDrawingPoints,
    applyTypedCoordinates,
    resetDrawing,
    getDrawnGraphic,
    beginEditingLocation,
    describeDrawnGraphic,
  } = useUscgSymbolDrawing({
    mapView,
  });

  // get :incidentId from the url when available in Incident Workspace
  // we cannot use useParams because UscgMapContextProvider wraps all Routes, it is not a child of any one Route
  const location = useLocation();
  const iapMatch = matchPath('/iap/:incidentId/*', location.pathname);
  const incidentIdParam = iapMatch?.params.incidentId;
  const incidentFilterId = incidentIdParam ?? stateIncidentFilterId;
  const { data: incident } = useExactIncident(incidentFilterId ?? '');
  const group = useCurrentGroup();
  const { data: apiKeyExternalLayersData } = useListExternalLayersQuery({
    filter: {
      authType: 'apiKey',
      groupGuid: group.group_guid ?? '',
      incidentId: incidentFilterId || undefined,
    },
    perPage: 200,
    include: ['incident'],
  });

  const { data: noAuthExternalLayersData } = useListExternalLayersQuery({
    filter: {
      authType: 'public',
      groupGuid: group.group_guid ?? '',
      incidentId: incidentFilterId || undefined,
    },
    perPage: 200,
    include: ['incident'],
  });

  const { data: orgExternalLayersData } = useListExternalLayersQuery({
    filter: {
      authType: 'userAuth',
      groupGuid: group.group_guid ?? '',
      incidentId: incidentFilterId || undefined,
    },
    perPage: 200,
    include: ['incident'],
  });

  useEnsureTennesseeNationalGuardLayers({
    groupGuid: group.group_guid,
  });

  useEnsureChickfilaLocationsLayer({
    groupGuid: group.group_guid,
  });

  useEnsureUsaCurrentWildfiresLayer({
    groupGuid: group.group_guid,
  });

  useEnsureHistoricalHurricanesDemo({
    mapView,
  });
  useEnsureOrgAssetsLayer(mapView);
  useAssetSymbolVisibilitySync(mapView);

  useEnsureMemphisThreatOrgActions();
  useEnsureUscgFocAors();
  useEnsureUscgFocSeThreatOrgActions();
  useEnsureUscgFocSeResourceSets();
  useEnsureUscgFocSeOrgEvent();
  // Archives the obsolete synthetic FOC cone (ERMA polygons replace it).
  useEnsureUscgFocSeStormLayer();
  useEnsureTennesseeMemphisOrgActions();
  useEnsureTennesseeMemphisResourceSets();
  useEnsureTennesseeMemphisOrgEvent();
  useEnsureTennesseeMemphisAssetLayer({ mapView });
  useEnsurePublicExternalLayers();
  useEnsureHeleneConeResourceSets();
  useEnsureHeleneConeOrgEvent();
  useEnsureEpaGulfOrgActions();
  useEnsureEpaGulfResourceSets();
  useEnsureEpaGulfOrgEvent();
  useEnsureDhsTropicalAutomationRule();
  useEnsureDhsRegion4ResourceSets();
  useEnsureDhsRegion4OrgEvent();
  useEnsureCnicTropicalAutomationRule();
  useEnsureCnicResourceSets();
  useEnsureCnicOrgEvent();
  useEnsureCnicHawaiiStormLayer();
  useEnsureOntarioWinterAutomationRule();
  useEnsureOntarioGtaResourceSets();
  useEnsureOntarioGtaOrgEvent();
  useEnsureOntarioGtaStormLayer();
  useEnsureEquinorAutomationRule();
  useEnsureEquinorGraneResourceSets();
  useEnsureEquinorGraneOrgEvent();
  useEnsureEquinorGraneIncident();
  useEnsureJapanAutomationRule();
  useEnsureJapanResourceSets();
  useEnsureJapanOrgEvent();
  useEnsureJapanIncident();
  useEnsureFacilityDemoAssetOrgActions();

  const { data: internalLayersData } = useListInternalLayersQuery({
    filter: {
      groupGuid: group.group_guid ?? '',
    },
    perPage: 200,
    include: ['incident'],
  });

  const { isLoading: isLoadingInternalFeatures } = useInternalLayerSync({
    mapView,
    internalLayerVisibilityById,
    incidentFilterId: incidentFilterId ?? null,
    selectedInternalLayerVersionByInternalLayerId,
    layersRef: internalGraphicsLayersRef,
  });

  useDefaultInternalLayerVersionSelection({
    setSelectedInternalLayerVersionByInternalLayerId,
  });

  // Sync AOR geometries into the USCG map, respecting AOR filter
  useUscgAorLayerSync({ mapView, isVisible: isAorVisible, aorFilterIds });

  // County choropleths for the latest power outage snapshot / ERI forecast (data only fetched while shown).
  usePowerOutageLayerSync({ mapView, isVisible: powerOutageLayerActive });
  useEriLayerSync({ mapView, isVisible: eriLayerActive, step: eriStep });

  // Sync the shapes drawn on the 215A's incident areas. Registered here rather
  // than seeded as a stored layer, which is what puts it in every workspace.
  useIncidentAreaLayerSync({ mapView, isVisible: isIncidentAreaVisible });

  // Sync TAK entities (ingested by pratus-tak). Poll only while TN Guard FF is on and layer visible.
  const { data: takEntitiesData } = useListTakEntitiesQuery({
    enabled: takLayerActive,
  });
  useTakLayerSync({
    mapView,
    takEntities: takEntitiesData?.data ?? [],
    isVisible: takLayerActive,
    suppressMapClicks: aiLayerPickMode,
  });

  const [hiddenIncidentIds, setHiddenIncidentIds] = useState<string[]>([]);
  const [hiddenActionIds, setHiddenActionIds] = useState<string[]>([]);
  const [visibleActionIds, setVisibleActionIds] = useState<string[] | null>(
    null
  );
  const [selectedActionIds, setSelectedActionIds] = useState<string[]>([]);
  const [focusedActionId, setFocusedActionId] = useState<string | null>(null);
  const [actionAreaFilter, setActionAreaFilter] =
    useState<ActionAreaFilter | null>(null);
  const { data: allIncidents = [] } = useAllIncidents();

  const mapIncidents = useMemo<IncidentListItemData[]>(() => {
    const aorNameById = new Map((aors ?? []).map((aor) => [aor.id, aor.name]));

    return buildIncidentListItems({ incidents: allIncidents, aorNameById });
  }, [allIncidents, aors]);

  useIncidentLayerSync({
    mapView,
    visibleIncidents: mapIncidents,
    hiddenIncidentIds,
    suppressMapClicks: aiLayerPickMode,
  });

  const { data: incidentActionsData = [] } = useIncidentActions(
    INCIDENT,
    incidentFilterId
  );
  const { data: orgActionsData = [] } = useOrgActions(
    incidentFilterId ? INCIDENT : ORGANIZATION
  );
  const mapActions = useMemo(
    () =>
      (incidentFilterId ? incidentActionsData : orgActionsData).filter(
        (action) => !action.archived
      ),
    [incidentFilterId, incidentActionsData, orgActionsData]
  );

  const handleActionGraphicClick = useCallback((actionId: string) => {
    setFocusedActionId(actionId);
  }, []);

  useActionLayerSync({
    mapView,
    actions: mapActions,
    hiddenActionIds,
    visibleActionIds,
    selectedActionIds,
    onActionGraphicClick: handleActionGraphicClick,
    suppressMapClicks: aiLayerPickMode,
  });

  const toggleIncidentHiddenOnMap = useCallback((incidentId: string) => {
    setHiddenIncidentIds((prev) =>
      prev.includes(incidentId)
        ? prev.filter((id) => id !== incidentId)
        : [...prev, incidentId]
    );
  }, []);

  const toggleActionHiddenOnMap = useCallback((actionId: string) => {
    setHiddenActionIds((prev) =>
      prev.includes(actionId)
        ? prev.filter((id) => id !== actionId)
        : [...prev, actionId]
    );
  }, []);

  useEffect(() => {
    setHiddenActionIds([]);
    setVisibleActionIds(null);
    setSelectedActionIds([]);
    setFocusedActionId(null);
    setActionAreaFilter(null);
  }, [incidentFilterId]);

  // When the list API omits incidentId it returns every scope for the group.
  // Keep hub (null incident) on Hub; incident workspace gets that incident + hub.
  const layerInCurrentScope = useCallback(
    (layer: { incidentId?: string | null }) =>
      isExternalLayerInCurrentScope(layer, incidentFilterId),
    [incidentFilterId]
  );

  const publicExternalLayers = useMemo(() => {
    // apiKey + public auth types both sync without user ArcGIS OAuth.
    return [
      ...(apiKeyExternalLayersData?.data ?? []),
      ...(noAuthExternalLayersData?.data ?? []),
    ].filter(layerInCurrentScope);
  }, [apiKeyExternalLayersData, noAuthExternalLayersData, layerInCurrentScope]);

  const orgExternalLayers = useMemo(() => {
    if (!isArcGISAuthorized) {
      return [];
    }

    return (orgExternalLayersData?.data ?? []).filter(layerInCurrentScope);
  }, [isArcGISAuthorized, orgExternalLayersData, layerInCurrentScope]);

  const internalLayersWithVisibilityDefaults = useMemo<
    Pick<InternalLayer, 'id' | 'defaultVisible' | 'incidentId'>[]
  >(() => internalLayersData?.data ?? [], [internalLayersData]);

  const previewExternalLayers = useMemo<
    Pick<
      ExternalLayer,
      | 'id'
      | 'arcgisItemId'
      | 'serviceUrl'
      | 'incident'
      | 'incidentId'
      | 'authType'
      | 'defaultVisible'
    >[]
  >(
    () =>
      previewLayers
        .filter(
          (
            layer
          ): layer is PreviewLayer & {
            id: string;
            authType: ExternalLayerAccessType;
          } => Boolean(layer.id && layer.authType)
        )
        .map((layer) => ({
          id: layer.id,
          arcgisItemId: layer.id,
          serviceUrl: layer.url,
          incident: null,
          incidentId: null,
          authType: layer.authType,
          defaultVisible: true, // Preview layers default to visible
        })),
    [previewLayers]
  );

  const externalLayersToSync = useMemo<
    Pick<
      ExternalLayer,
      | 'id'
      | 'arcgisItemId'
      | 'serviceUrl'
      | 'incident'
      | 'incidentId'
      | 'authType'
      | 'defaultVisible'
    >[]
  >(() => {
    const merged = new Map<
      string,
      Pick<
        ExternalLayer,
        | 'id'
        | 'arcgisItemId'
        | 'serviceUrl'
        | 'incident'
        | 'incidentId'
        | 'authType'
        | 'defaultVisible'
      >
    >();

    for (const layer of [
      ...publicExternalLayers,
      ...orgExternalLayers,
      ...previewExternalLayers,
    ]) {
      merged.set(String(layer.id), layer);
    }

    return Array.from(merged.values());
  }, [orgExternalLayers, previewExternalLayers, publicExternalLayers]);

  useExternalLayerSync({
    mapView,
    externalLayers: externalLayersToSync,
    externalLayerVisibilityById,
    setExternalLayerVisibilityById,
    addedExternalLayersRef,
    setIsExternalLayersReady,
    setIsPublicExternalLayersReady,
    setIsProtectedExternalLayersReady,
    setExternalLayerLoadErrorById,
    incidentFilterId,
  });

  useDefaultLayerVisibilityById({
    layers: internalLayersWithVisibilityDefaults,
    setVisibilityById: setInternalLayerVisibilityById,
    incidentFilterId,
  });

  const equinorVisibilityForcedRef = useRef(new Set<string>());
  useEffect(() => {
    if (!equinorEnabled) return;
    const names = new Set([
      EQUINOR_DIVISIONS_LAYER_NAME,
      EQUINOR_OIL_SLICK_LAYER_TITLE,
      EQUINOR_GRANE_ASSETS_LAYER_TITLE,
    ]);
    const toShow = (internalLayersData?.data ?? []).filter(
      (layer) =>
        names.has(layer.name) &&
        !equinorVisibilityForcedRef.current.has(layer.id)
    );
    if (toShow.length === 0) return;

    setInternalLayerVisibilityById((prev) => {
      const next = new Map(prev);
      for (const layer of toShow) {
        next.set(layer.id, true);
        equinorVisibilityForcedRef.current.add(layer.id);
      }
      return next;
    });
  }, [equinorEnabled, internalLayersData]);

  const japanVisibilityForcedRef = useRef(new Set<string>());
  useEffect(() => {
    if (!japanEnabled) return;
    const names = new Set(JAPAN_INCIDENT_LAYER_NAMES);
    const toShow = (internalLayersData?.data ?? []).filter(
      (layer) =>
        names.has(layer.name) && !japanVisibilityForcedRef.current.has(layer.id)
    );
    if (toShow.length === 0) return;

    setInternalLayerVisibilityById((prev) => {
      const next = new Map(prev);
      for (const layer of toShow) {
        next.set(layer.id, true);
        japanVisibilityForcedRef.current.add(layer.id);
      }
      return next;
    });
  }, [japanEnabled, internalLayersData]);

  // Frame Tokyo Bay once per page load so every capture starts from the same
  // view; after that the user's own panning wins. A failed or interrupted
  // framing is not retried, so it can never jump the camera mid-drag.
  useEffect(() => {
    if (!japanEnabled || !mapView || japanFramedThisSession) return;
    japanFramedThisSession = true;
    void mapView
      .goTo(
        new Extent({ ...JAPAN_MAP_EXTENT, spatialReference: { wkid: 4326 } }),
        { duration: 0 }
      )
      .catch(() => undefined);
  }, [japanEnabled, mapView]);
  const ontarioVisibilityForcedRef = useRef(new Set<string>());
  useEffect(() => {
    if (!ontarioEnabled) return;
    const toShow = (internalLayersData?.data ?? []).filter(
      (layer) =>
        layer.name === ONTARIO_GTA_STORM_LAYER_NAME &&
        !ontarioVisibilityForcedRef.current.has(layer.id)
    );
    if (toShow.length === 0) return;

    setInternalLayerVisibilityById((prev) => {
      const next = new Map(prev);
      for (const layer of toShow) {
        next.set(layer.id, true);
        ontarioVisibilityForcedRef.current.add(layer.id);
      }
      return next;
    });
  }, [ontarioEnabled, internalLayersData]);

  /** FOC uses ERMA Helene polygons — force-hide the obsolete synthetic cone. */
  const focStormHiddenRef = useRef(new Set<string>());
  useEffect(() => {
    if (!uscgFocEnabled) return;
    const toHide = (internalLayersData?.data ?? []).filter(
      (layer) =>
        (layer.name === USCG_FOC_SE_STORM_LAYER_NAME ||
          layer.name.includes(USCG_FOC_SE_STORM_LOCATION_NAME)) &&
        !focStormHiddenRef.current.has(layer.id)
    );
    if (toHide.length === 0) return;

    setInternalLayerVisibilityById((prev) => {
      const next = new Map(prev);
      for (const layer of toHide) {
        next.set(layer.id, false);
        focStormHiddenRef.current.add(layer.id);
      }
      return next;
    });
  }, [uscgFocEnabled, internalLayersData]);

  const clearLocationGraphicLayer = useCallback(() => {
    locationGraphicLayerRef.current.removeAll();
  }, []);

  const { drawLocationOnMap } = useLocationGraphicLayer({
    mapView,
    drawingLayerRef: locationGraphicLayerRef,
    clearDrawingLayer: clearLocationGraphicLayer,
  });

  const clearReportGeofenceLayer = useCallback(() => {
    reportGeofenceLayerRef.current.removeAll();
  }, []);

  const { drawLocationOnMap: drawReportGeofenceOnMap } =
    useLocationGraphicLayer({
      mapView,
      drawingLayerRef: reportGeofenceLayerRef,
      clearDrawingLayer: clearReportGeofenceLayer,
      popupTitle: 'Report area',
    });

  const showReportGeofence = useCallback(
    (geometry: Geometry, label?: string, ownerId?: string) => {
      reportGeofenceOwnerRef.current = ownerId ?? null;
      drawReportGeofenceOnMap(geometry, label);
    },
    [drawReportGeofenceOnMap]
  );

  const clearReportGeofence = useCallback(
    (ownerId?: string) => {
      if (
        ownerId &&
        reportGeofenceOwnerRef.current &&
        reportGeofenceOwnerRef.current !== ownerId
      ) {
        return;
      }
      reportGeofenceOwnerRef.current = null;
      clearReportGeofenceLayer();
    },
    [clearReportGeofenceLayer]
  );

  useEffect(() => {
    if (!mapView?.map) return;

    const locationGraphicLayer = locationGraphicLayerRef.current;
    mapView.map.add(locationGraphicLayer);

    return () => {
      mapView.map.remove(locationGraphicLayer);
      clearLocationGraphicLayer();
    };
  }, [clearLocationGraphicLayer, mapView]);

  useEffect(() => {
    if (!mapView?.map) return;

    const reportGeofenceLayer = reportGeofenceLayerRef.current;
    mapView.map.add(reportGeofenceLayer);

    return () => {
      mapView.map.remove(reportGeofenceLayer);
      clearReportGeofenceLayer();
    };
  }, [clearReportGeofenceLayer, mapView]);

  useEffect(() => {
    if (!incident?.location) {
      clearLocationGraphicLayer();
      return;
    }

    drawLocationOnMap(incident.location);
  }, [clearLocationGraphicLayer, drawLocationOnMap, incident?.location]);

  const getAddedExternalLayers = useCallback(
    (layerId: string) => addedExternalLayersRef.current.get(layerId),
    []
  );

  const toggleExternalLayerVisibility = useCallback(
    (layerId: string) => {
      const isVisible = !(externalLayerVisibilityById.get(layerId) ?? false);

      setExternalLayerVisibilityById((prev) =>
        new Map(prev).set(layerId, isVisible)
      );

      getAddedExternalLayers(layerId)?.forEach((layer) => {
        layer.visible = isVisible;
      });
    },
    [externalLayerVisibilityById, getAddedExternalLayers]
  );

  const isExternalLayerVisible = useCallback(
    (layerId: string) => externalLayerVisibilityById.get(layerId) ?? false,
    [externalLayerVisibilityById]
  );

  const toggleInternalLayerVisibility = useCallback((layerId: string) => {
    setInternalLayerVisibilityById((prev) => {
      const isVisible = prev.get(layerId) ?? false;
      return new Map(prev).set(layerId, !isVisible);
    });
  }, []);

  const setInternalLayerVisible = useCallback(
    (layerId: string, visible: boolean) => {
      setInternalLayerVisibilityById((prev) => {
        if (prev.get(layerId) === visible) return prev;
        return new Map(prev).set(layerId, visible);
      });
    },
    []
  );

  const isInternalLayerVisible = useCallback(
    (layerId: string) => internalLayerVisibilityById.get(layerId) ?? false,
    [internalLayerVisibilityById]
  );

  const setSelectedInternalLayerVersion = useCallback(
    (layerId: string, version: InternalLayerVersion) => {
      setSelectedInternalLayerVersionByInternalLayerId((prev) => ({
        ...prev,
        [layerId]: version,
      }));
    },
    []
  );

  const getSelectedVersionForLayer = useCallback(
    (layerId: string): InternalLayerVersion | undefined => {
      return selectedInternalLayerVersionByInternalLayerId[layerId];
    },
    [selectedInternalLayerVersionByInternalLayerId]
  );

  const availableMapLayerItems = useMemo(
    (): AvailableMapLayerItem[] =>
      buildMapLayerCatalog({
        publicLayers: publicExternalLayers,
        orgLayers: orgExternalLayers,
        previewLayers: previewExternalLayers,
        internalLayers: internalLayersData?.data ?? [],
        isExternalVisible: isExternalLayerVisible,
        isInternalVisible: isInternalLayerVisible,
        externalLoadErrors: externalLayerLoadErrorById,
      }),
    [
      externalLayerLoadErrorById,
      internalLayersData?.data,
      isExternalLayerVisible,
      isInternalLayerVisible,
      orgExternalLayers,
      previewExternalLayers,
      publicExternalLayers,
    ]
  );

  const visibleMapLayerItems = useMemo(
    (): VisibleMapLayerItem[] =>
      availableMapLayerItems
        .filter((item) => item.visible)
        .map(({ id, name, kind }) => ({ id, name, kind })),
    [availableMapLayerItems]
  );

  const getVisibleLayersForAI = useCallback((): AiLayerPickPayload[] => {
    const visible: AiLayerPickPayload[] = [];
    const seenExternal = new Set<string>();
    for (const layer of [
      ...publicExternalLayers,
      ...orgExternalLayers,
      ...previewExternalLayers,
    ]) {
      const id = String(layer.id);
      if (seenExternal.has(id)) continue;
      if (!isExternalLayerVisible(id)) continue;
      seenExternal.add(id);
      visible.push({
        kind: 'external',
        id,
        name:
          ('name' in layer && typeof layer.name === 'string' && layer.name) ||
          'Untitled external layer',
        serviceUrl: layer.serviceUrl,
        arcgisItemId: layer.arcgisItemId,
      });
    }
    for (const layer of internalLayersData?.data ?? []) {
      const id = String(layer.id);
      if (!isInternalLayerVisible(id)) continue;
      const version = getSelectedVersionForLayer(id);
      visible.push({
        kind: 'internal',
        id,
        name: layer.name || 'Untitled internal layer',
        versionId: version?.id
          ? String(version.id)
          : layer.draftVersionId
            ? String(layer.draftVersionId)
            : undefined,
      });
    }
    return visible;
  }, [
    getSelectedVersionForLayer,
    internalLayersData?.data,
    isExternalLayerVisible,
    isInternalLayerVisible,
    orgExternalLayers,
    previewExternalLayers,
    publicExternalLayers,
  ]);

  const { startAiAreaDraw, cancelAiAreaDraw, aiAreaDrawActive } = useAiMapPick({
    mapView,
    isMapVisible,
    aiLayerPickMode,
    symbolDrawingActive: symbolDrawingState.geometryType != null,
    addedExternalLayersRef,
    internalGraphicsLayersRef,
    selectedInternalLayerVersionByInternalLayerId,
    getVisibleLayersForAI,
    pickAiLayer,
    pickAiDrawnArea,
    onDrawCancelled: () => aiDrawnAreaCancelHandlerRef.current?.(),
  });

  const toggleAorVisibility = useCallback(
    () => setIsAorVisible((prev) => !prev),
    []
  );

  const toggleIncidentAreaVisibility = useCallback(
    () => setIsIncidentAreaVisible((prev) => !prev),
    []
  );

  const toggleTakVisibility = useCallback(
    () => setIsTakVisible((prev) => !prev),
    []
  );

  const togglePowerOutageVisibility = useCallback(
    () => setIsPowerOutageVisible((prev) => !prev),
    []
  );

  const toggleEriVisibility = useCallback(
    () => setIsEriVisible((prev) => !prev),
    []
  );

  const revealMapLayer = useCallback(
    (kind: 'internal' | 'external', layerId: string) => {
      if (kind === 'internal') {
        setInternalLayerVisible(layerId, true);
        const selected = getSelectedVersionForLayer(layerId);
        const graphicsLayer = selected
          ? internalGraphicsLayersRef.current.get(selected.id)
          : undefined;
        if (graphicsLayer) graphicsLayer.visible = true;
        return;
      }

      setExternalLayerVisibilityById((prev) => {
        if (prev.get(layerId) === true) return prev;
        return new Map(prev).set(layerId, true);
      });
      getAddedExternalLayers(layerId)?.forEach((layer) => {
        layer.visible = true;
      });
    },
    [
      getAddedExternalLayers,
      getSelectedVersionForLayer,
      setInternalLayerVisible,
    ]
  );

  const ensureExternalLayers = useCallback(
    async (layerId: string) => {
      revealMapLayer('external', layerId);
      const ready = () => getAddedExternalLayers(layerId) ?? [];
      let layers = ready();
      if (layers.length > 0) return layers;

      for (let attempt = 0; attempt < 40; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 150));
        layers = ready();
        if (layers.length > 0) {
          layers.forEach((layer) => {
            layer.visible = true;
          });
          return layers;
        }
      }
      return layers;
    },
    [getAddedExternalLayers, revealMapLayer]
  );

  const zoomToLocation = useCallback(
    ({
      longitude,
      latitude,
      zoom = 10,
    }: {
      longitude: number;
      latitude: number;
      zoom?: number;
    }) => zoomMapViewToLocation({ mapView, longitude, latitude, zoom }),
    [mapView]
  );

  const zoomToGraphic = useCallback(
    ({ graphic, zoom }: { graphic: __esri.Graphic; zoom?: number }) =>
      zoomMapViewToGraphic({ mapView, graphic, zoom }),
    [mapView]
  );

  const focusFeatureOnMap = useCallback(
    async ({
      graphic,
      layer,
    }: {
      graphic: __esri.Graphic;
      layer?: { kind: 'external' | 'internal'; layerId: string };
    }) => {
      if (layer?.kind === 'internal') {
        revealMapLayer('internal', layer.layerId);
      }
      if (layer?.kind === 'external') {
        await ensureExternalLayers(layer.layerId);
      }
      return focusFeatureOnMapUtil({ mapView, graphic });
    },
    [ensureExternalLayers, mapView, revealMapLayer]
  );

  const getInternalFeatureGraphic = useCallback(
    (internalLayerId: string, featureId: string): __esri.Graphic | null => {
      const selectedVersion = getSelectedVersionForLayer(internalLayerId);
      if (!selectedVersion) return null;

      const graphicsLayer = internalGraphicsLayersRef.current.get(
        selectedVersion.id
      );
      if (!graphicsLayer) return null;

      return (
        graphicsLayer.graphics
          .toArray()
          .find((g) => String(g.attributes?.id ?? '') === String(featureId)) ??
        null
      );
    },
    [getSelectedVersionForLayer]
  );

  const focusLayerFeatureOnMap = useCallback(
    async ({
      kind,
      layerId,
      featureId,
      geometry,
      attributes,
      title,
      mapLayerTitle,
    }: {
      kind: 'external' | 'internal';
      layerId: string;
      featureId: string;
      geometry?: unknown;
      attributes?: Record<string, unknown> | null;
      title?: string | null;
      mapLayerTitle?: string | null;
    }) => {
      if (!mapView) return;
      if (kind === 'internal') revealMapLayer('internal', layerId);

      if (kind === 'internal') {
        const fromPayload = geometry
          ? buildFallbackFeatureGraphic({
              geometry,
              attributes: { ...(attributes ?? {}), id: featureId },
              title,
            })
          : null;
        if (fromPayload) {
          await focusFeatureOnMapUtil({ mapView, graphic: fromPayload });
          return;
        }
        const live = getInternalFeatureGraphic(layerId, featureId);
        if (live) {
          await focusFeatureOnMapUtil({ mapView, graphic: live });
        }
        return;
      }

      await ensureExternalLayers(layerId);
      // external — query live FeatureLayers by OBJECTID / feature id first
      const layers = getAddedExternalLayers(layerId) ?? [];
      const featureLayers = collectFeatureLayers(layers);
      const wantedTitle = mapLayerTitle?.trim().toLowerCase() ?? '';
      const wantedStorm = String(attributes?.STORMNAME ?? '').trim();
      const searchLayers = wantedTitle
        ? featureLayers.filter(
            (layer) => layer.title?.trim().toLowerCase() === wantedTitle
          )
        : featureLayers;
      for (const layer of searchLayers) {
        try {
          const objectId = Number(featureId);
          const where = Number.isFinite(objectId)
            ? `${layer.objectIdField || 'OBJECTID'} = ${objectId}`
            : `1=0`;
          const { features } = await layer.queryFeatures({
            where,
            returnGeometry: true,
            outFields: ['*'],
            outSpatialReference: WGS84_SPATIAL_REFERENCE,
            num: 1,
          });
          const hit = features[0];
          if (hit?.geometry) {
            const hitStorm = String(
              hit.attributes?.STORMNAME ?? hit.attributes?.stormname ?? ''
            ).trim();
            if (
              wantedStorm &&
              hitStorm &&
              hitStorm.toLowerCase() !== wantedStorm.toLowerCase()
            ) {
              continue;
            }
            applyStoredAttributePopup(hit, title);
            await focusFeatureOnMapUtil({ mapView, graphic: hit });
            return;
          }
        } catch {
          // try next layer / fall through to geometry fallback
        }
      }

      const fallback = buildFallbackFeatureGraphic({
        geometry,
        attributes: {
          ...(attributes ?? {}),
          OBJECTID: featureId,
          name: title || undefined,
        },
        title,
      });
      if (fallback) {
        applyStoredAttributePopup(fallback, title);
        await focusFeatureOnMapUtil({ mapView, graphic: fallback });
      }
    },
    [
      ensureExternalLayers,
      mapView,
      getInternalFeatureGraphic,
      getAddedExternalLayers,
      revealMapLayer,
    ]
  );

  const zoomToExternalLayer = useCallback(
    async ({ externalLayerId }: { externalLayerId: string }) => {
      if (!mapView) return;

      const layers = getAddedExternalLayers(externalLayerId) ?? [];
      if (layers.length === 0) return;

      const extents = await Promise.all(
        layers.map((l) => getLayerExtent(l, mapView))
      );
      const merged = extents
        .filter((e): e is __esri.Extent => e !== null)
        .reduce<__esri.Extent | null>(
          (acc, e) => (acc ? acc.union(e) : e),
          null
        );

      if (merged) {
        return mapView.goTo(merged.expand(1.2), { duration: 500 });
      }
    },
    [mapView, getAddedExternalLayers]
  );

  const zoomToInternalLayer = useCallback(
    ({ internalLayerId }: { internalLayerId: string }) => {
      if (!mapView) return;

      const selectedVersion = getSelectedVersionForLayer(internalLayerId);
      if (!selectedVersion) return;

      const graphicsLayer = internalGraphicsLayersRef.current.get(
        selectedVersion.id
      );
      if (!graphicsLayer) return;

      const graphics = graphicsLayer.graphics.toArray();
      if (graphics.length === 0) return;

      return mapView.goTo(graphics, { duration: 500 });
    },
    [getSelectedVersionForLayer, mapView]
  );

  const focusLayerOnMap = useCallback(
    async ({
      kind,
      layerId,
      name,
      lastUpdated,
    }: {
      kind: 'external' | 'internal';
      layerId: string;
      name: string;
      lastUpdated?: string | null;
    }) => {
      if (!mapView) return;
      if (kind === 'internal') revealMapLayer('internal', layerId);

      let centroid: __esri.Point | null = null;

      if (kind === 'external') {
        const layers = await ensureExternalLayers(layerId);
        if (layers.length === 0) return;

        const extents = await Promise.all(
          layers.map((l) => getLayerExtent(l, mapView))
        );
        const merged = extents
          .filter((e): e is __esri.Extent => e !== null)
          .reduce<__esri.Extent | null>(
            (acc, e) => (acc ? acc.union(e) : e),
            null
          );
        if (!merged?.center) return;
        centroid = merged.center;
      } else {
        const selectedVersion = getSelectedVersionForLayer(layerId);
        if (!selectedVersion) return;

        const graphicsLayer = internalGraphicsLayersRef.current.get(
          selectedVersion.id
        );
        if (!graphicsLayer) return;

        const graphics = graphicsLayer.graphics.toArray();
        if (graphics.length === 0) return;

        const geometries = graphics
          .map((g) => g.geometry)
          .filter((g): g is __esri.GeometryUnion => g != null);
        if (geometries.length === 0) return;

        const unioned = geometryEngine.union(geometries);
        const first = geometries[0];
        if (unioned?.extent?.center) {
          centroid = unioned.extent.center;
        } else if (first?.type === 'point') {
          centroid = first as Point;
        } else if (first?.extent?.center) {
          centroid = first.extent.center;
        }
      }

      if (!centroid) return;

      await mapView.goTo(
        { target: centroid, zoom: LAYER_CENTROID_ZOOM },
        { duration: 500 }
      );

      const formatted = lastUpdated ? formatDateTime(lastUpdated) : '—';
      const lastUpdatedText =
        !lastUpdated || formatted === 'N/A' ? '—' : formatted;

      await mapView.openPopup({
        title: name || 'Layer',
        location: centroid,
        content: `Last updated: ${lastUpdatedText}`,
      });
    },
    [ensureExternalLayers, mapView, getSelectedVersionForLayer, revealMapLayer]
  );

  const getExternalLayerFeatures = useCallback(
    async (externalLayerId: string) => {
      const layers = getAddedExternalLayers(externalLayerId);

      if (!layers?.length) {
        return [];
      }
      return queryExternalLayerFeatures(layers);
    },
    [getAddedExternalLayers]
  );

  const isExternalLayerLoadError = useCallback(
    (externalLayerId: string) =>
      externalLayerLoadErrorById.get(externalLayerId) ?? false,
    [externalLayerLoadErrorById]
  );

  const captureMapSketchFile = useCallback(async () => {
    if (!mapView) return;

    await mapView.when();

    const locationGraphics = locationGraphicLayerRef.current.graphics.toArray();
    if (locationGraphics.length === 0) return;

    // Clear padding to ensure we capture the entire map, then restore it afterwards.
    const previousPadding = { ...mapView.padding };
    mapView.padding = { left: 0, top: 0, right: 0, bottom: 0 };

    await mapView.goTo(locationGraphics);

    // Wait for the view to stop moving
    await reactiveUtil.whenOnce(() => mapView.stationary && !mapView.updating);

    const screenshot = await mapView.takeScreenshot({
      format: 'png',
      height: MAP_BENCH_HEIGHT,
      width: MAP_BENCH_WIDTH,
    });
    const response = await fetch(screenshot.dataUrl);
    const blob = await response.blob();

    mapView.padding = previousPadding;

    return new File([blob], 'mapSketch.png', { type: 'image/png' });
  }, [mapView]);

  // Auto-zoom to user's home AOR when map view and aor query has finished
  useEffect(() => {
    if (!homeAorId || isHomeAorDescendantsLoading) return;

    const descendantIds = homeAorDescendants?.map((d) => d.id);
    performZoomToHomeAor(mapView, aors, homeAorId, descendantIds, {
      currentAorFilterIds: aorFilterIds,
      isAorVisible,
      onFilterChange: setAorFilterIds,
      onVisibilityChange: setIsAorVisible,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapView,
    aors,
    homeAorId,
    homeAorDescendants,
    isHomeAorDescendantsLoading,
    setIsAorVisible,
    setAorFilterIds,
  ]);

  // Setup offscreen div used as a \"bench\" container for initializing the USCG MapView/WebMap.
  useEffect(() => {
    const bench = document.getElementById(
      MAP_BENCH_ID
    ) as HTMLDivElement | null;
    if (!bench) return;

    const originalBenchStyle = bench.style.cssText;
    bench.style.cssText = `position:absolute;width:${MAP_BENCH_WIDTH}px;height:${MAP_BENCH_HEIGHT}px;overflow:hidden;left:-9999px;top:0`;

    const webMap =
      mapType === 'uscg'
        ? new WebMap({
            portalItem: { id: USCG_PORTAL_ITEM_ID },
            ground: mapViewMode === '3d' ? 'world-elevation' : undefined,
          })
        : new WebMap({
            basemap: HYBRID,
            ground: mapViewMode === '3d' ? 'world-elevation' : undefined,
          });

    const snapshot = viewpointRef.current;
    const fallback = {
      center: US_INITIAL_VIEW.center,
      zoom: US_INITIAL_VIEW.zoom,
    };

    const newMapView: ArcgisMapView =
      mapViewMode === '3d'
        ? new SceneView({
            map: webMap,
            container: bench,
            viewingMode: 'global',
            qualityProfile: 'medium',
            camera: sceneCameraFromViewpoint(snapshot, fallback),
            environment: {
              lighting: {
                type: 'sun',
                directShadowsEnabled: true,
              },
              atmosphereEnabled: true,
              starsEnabled: true,
            },
          })
        : snapshot
          ? new MapView({
              map: webMap,
              container: bench,
              ...mapCenterZoomFromViewpoint(snapshot, fallback),
            })
          : mapType === 'hybrid'
            ? new MapView({
                map: webMap,
                container: bench,
                center: US_INITIAL_VIEW.center,
                zoom: US_INITIAL_VIEW.zoom,
              })
            : new MapView({
                map: webMap,
                container: bench,
              });

    // No padding needed — sidebar and map are flex siblings, not overlapping
    newMapView.padding = { left: 0, top: 0, right: 0, bottom: 0 };
    newMapView.ui.move('zoom', 'top-right');
    const addressSearch = new Search({
      view: newMapView,
      includeDefaultSources: false,
      popupEnabled: true,
    });
    addressSearch.viewModel.sources = [
      new LocatorSearchSource({
        url: 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer',
        singleLineFieldName: 'SingleLine',
        name: 'Addresses',
        placeholder: 'Search address',
      }),
    ];
    newMapView.ui.add(addressSearch, 'bottom-right');
    let stopZoomOutWatch: { remove: () => void } | undefined;
    newMapView.when(() => {
      if (newMapView.destroyed) return;
      try {
        applyZoomOutConstraints(newMapView);
      } catch {
        // A bad constraint must not leave the view on the hidden bench.
      }
      // Attach even if the constraint step throws, or the view stays on the
      // hidden bench and the pane stays blank.
      setMapView(newMapView);
      stopZoomOutWatch = reactiveUtil.watch(
        () => [
          newMapView.width,
          newMapView.height,
          newMapView.zoom,
          newMapView.center?.latitude,
        ],
        () => applyZoomOutConstraints(newMapView)
      );
    });

    return () => {
      stopZoomOutWatch?.remove();
      newMapView.ui.remove(addressSearch);
      addressSearch.destroy();
      viewpointRef.current =
        snapshotArcgisViewpoint(newMapView) ?? viewpointRef.current;
      newMapView.destroy();
      webMap.destroy();
      bench.style.cssText = originalBenchStyle;
      setMapView(null);
    };
  }, [mapType, mapViewMode]);

  const value = {
    mapView,
    mapViewElementRef,
    isMapVisible,
    setIsMapVisible,
    mapType,
    setMapType,
    mapViewMode,
    setMapViewMode,
    toggleExternalLayerVisibility,
    isExternalLayerVisible,
    toggleInternalLayerVisibility,
    setInternalLayerVisible,
    isInternalLayerVisible,
    visibleMapLayerItems,
    availableMapLayerItems,
    startDrawing,
    setPolygonDrawingColor,
    setPolygonDrawingOpacity,
    stopDrawing,
    addPendingPoint,
    addPendingPolyline,
    addPendingPolygon,
    clearPendingGraphics,
    removePendingGraphicsByPendingGraphicId,
    symbolDrawingState,
    connectDrawingPoints,
    applyTypedCoordinates,
    resetDrawing,
    getDrawnGraphic,
    beginEditingLocation,
    describeDrawnGraphic,

    zoomToLocation,
    zoomToGraphic,
    zoomToExternalLayer,
    zoomToInternalLayer,
    focusLayerOnMap,
    focusFeatureOnMap,
    getInternalFeatureGraphic,
    focusLayerFeatureOnMap,
    getExternalLayerFeatures,
    getAddedExternalLayers,

    isExternalLayerLoadError,
    externalLayerLoadErrorById,

    aorFilterIds,
    setAorFilterIds,

    externalLayerTimeFilterById,
    setExternalLayerTimeFilter,

    legendClassFilterByLayerId,
    setLegendClassFilter,
    setLayerDefinitionExpressionTimePart,

    aiLayerPickMode,
    setAiLayerPickMode,
    registerAiLayerPickHandler,
    pickAiLayer,
    registerAiDrawnAreaHandler,
    pickAiDrawnArea,
    registerAiDrawnAreaCancelHandler,
    areaDrawControlsOnMap,
    getVisibleLayersForAI,
    startAiAreaDraw,
    cancelAiAreaDraw,
    aiAreaDrawActive,
    showReportGeofence,
    clearReportGeofence,

    incidentFilterId,
    setIncidentFilterId,

    hiddenIncidentIds,
    setHiddenIncidentIds,
    toggleIncidentHiddenOnMap,

    hiddenActionIds,
    setHiddenActionIds,
    toggleActionHiddenOnMap,

    visibleActionIds,
    setVisibleActionIds,
    selectedActionIds,
    setSelectedActionIds,
    focusedActionId,
    setFocusedActionId,
    actionAreaFilter,
    setActionAreaFilter,

    toggleAorVisibility,
    isAorVisible,
    setIsAorVisible,

    toggleIncidentAreaVisibility,
    isIncidentAreaVisible,

    toggleTakVisibility,
    isTakVisible: takLayerActive,

    togglePowerOutageVisibility,
    isPowerOutageVisible: powerOutageLayerActive,
    toggleEriVisibility,
    isEriVisible: eriLayerActive,
    eriStep,
    setEriStep,

    setSelectedInternalLayerVersion,
    selectedInternalLayerVersionByInternalLayerId,
    getSelectedVersionForLayer,
    sidebarSelectedInternalLayer,
    setSidebarSelectedInternalLayer,
    isLoadingInternalFeatures,
    isArcGISAuthorized,
    isCheckingArcGISAuthorization,
    authorizeArcGIS,
    setIsArcGISAuthorized,
    isPublicExternalLayersReady,
    isProtectedExternalLayersReady:
      !isArcGISAuthorized || isProtectedExternalLayersReady,
    isExternalLayersReady,
    captureMapSketchFile,

    previewLayers,
    setPreviewLayers,
  };

  return (
    <UscgMapContext.Provider value={value}>{children}</UscgMapContext.Provider>
  );
};

/**
 * Resolves the map bounding box for a layer (e.g. coordinates we can use to
 * zoom-to-layer).
 */
async function getLayerExtent(
  layer: __esri.Layer,
  mapView: ArcgisMapView
): Promise<__esri.Extent | null> {
  if (layer instanceof GroupLayer) {
    const childExtents = await Promise.all(
      layer.allLayers.toArray().map((child) => getLayerExtent(child, mapView))
    );
    return childExtents
      .filter((e): e is __esri.Extent => e !== null)
      .reduce<__esri.Extent | null>((acc, e) => (acc ? acc.union(e) : e), null);
  }

  if (layer instanceof FeatureLayer) {
    try {
      const { extent } = await layer.queryExtent({ where: '1=1' });
      if (extent) return extent;
    } catch (err) {
      console.error('Failed to get extent for FeatureLayer', err);
    }
  }

  if (layer instanceof MapImageLayer) {
    // MapImageLayer.fullExtent can include non-visible sublayers, so we merge only visible sublayer extents
    // to ensure zoom-to-layer matches what the user is currently viewing.
    const sublayers = layer.allSublayers?.toArray() ?? [];
    const visibleSubLayers = sublayers.filter((s) => s.visible);

    const sublayerExtents = await Promise.all(
      visibleSubLayers.map(async (sublayer) => {
        try {
          // createFeatureLayer() only works for queryable sublayers
          const featureLayer = await sublayer.createFeatureLayer();
          if (!featureLayer) return null;

          await featureLayer.load();
          // queryExtent() gives us the minimum bounding box that will cover all features in the sublayer
          const { extent } = await featureLayer.queryExtent({ where: '1=1' });
          featureLayer.destroy();
          if (extent) {
            return extent;
          }
        } catch (err) {
          console.error('Failed to get extent for MapImageLayer sublayer', err);
        }

        return null;
      })
    );
    // extent.union(nextExtent) gives us the minimum bounding box that will cover all features in all sublayers.
    const merged = sublayerExtents
      .filter((e): e is __esri.Extent => e !== null)
      .reduce<__esri.Extent | null>((acc, e) => (acc ? acc.union(e) : e), null);
    if (merged) {
      return merged;
    }
  }

  return layer.fullExtent ?? null;
}
